import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs, query, orderBy, documentId } from "firebase/firestore";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { slugify } from "../../slugify";
import { CartIcon } from "../../components/ActionIcons";
import { IMAGE_COMING_SOON } from "../../imageComingSoon";
import "./SimilarProducts.css";

// FIELD_PATTERNS for extracting part number / title from product
const FIELD_PATTERNS = {
  partNo: [/part.?no/i, /part.?number/i, /model.?no/i, /biba.?part/i],
  title: [/^product.?name$/i, /^tit+le$/i, /^name$/i],
};

// Same "Active Area • TFT Interface • Diagonal Size • Outline Area •
// Resolution" spec line used on the main product grid card, so the FBT
// card reads the same way. Rendered in the same order as they appear
// there, skipping any that aren't present on this particular product.
const SPEC_FIELDS = [
  { label: "Active Area", patterns: [/active.?area/i] },
  { label: "TFT Interface", patterns: [/(tft|display).?interface/i, /^interface$/i] },
  { label: "Diagonal Size", patterns: [/diagonal.?size/i] },
  { label: "Outline Area", patterns: [/outline.?area/i] },
  { label: "Resolution", patterns: [/^resolution$/i] },
];

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

// Some products have both a "title" and a "Title" field (a data-entry
// artifact), and Firestore map-field key order isn't guaranteed stable
// across reads, so picking the first pattern match arbitrarily flips
// between the empty one and the real one on different loads. Prefer
// whichever match actually has a value over an empty one.
const matchField = (fieldsObj, patterns) => {
  const entries = Object.entries(fieldsObj || {}).filter(([key]) => testKey(key, patterns));
  if (entries.length === 0) return "";
  const nonEmpty = entries.find(([, value]) => value !== "" && value != null);
  return (nonEmpty || entries[0])[1] ?? "";
};

const buildSpecSummary = (fieldsObj) => {
  const parts = SPEC_FIELDS.map(({ label, patterns }) => {
    const value = matchField(fieldsObj, patterns);
    return value ? `${label}: ${value}` : null;
  }).filter(Boolean);
  return parts.join(" • ");
};

// How many cards are visible at once — scales with viewport width so the
// row keeps filling out horizontally on larger screens instead of only
// ever showing a single card.
const getVisibleCount = () => {
  if (typeof window === "undefined") return 4;
  const w = window.innerWidth;
  if (w > 1200) return 4;
  if (w > 900) return 3;
  if (w > 600) return 2;
  return 1;
};

const SimilarProducts = ({ products = [] }) => {
  const [productsWithData, setProductsWithData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Keep the row's visible card count in sync with viewport width.
  useEffect(() => {
    const onResize = () => setVisibleCount(getVisibleCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch product details including images, routes, and part numbers
  useEffect(() => {
    const fetchProductData = async () => {
      if (!products || products.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const productsWithAllData = await Promise.all(
          products.map(async (item) => {
            try {
              // Fetch full product document to get image and part number
              const productRef = doc(
                db,
                "product_categories",
                item.categoryId,
                "subCategories",
                item.subCategoryId,
                "productData",
                item.productId
              );

              const productSnap = await getDoc(productRef);
              let image = IMAGE_COMING_SOON;
              let partNo = "";
              let title = "";
              let specSummary = "";

              if (productSnap.exists()) {
                const fields = productSnap.data().fields || {};

                // Get part number from fields
                partNo = matchField(fields, FIELD_PATTERNS.partNo) || item.productId;
                title = matchField(fields, FIELD_PATTERNS.title);
                specSummary = buildSpecSummary(fields);

                // If imageUrl is in the main data, use it
                if (item.imageUrl) {
                  image = item.imageUrl;
                } else {
                  // Otherwise, Tixaru's own Front Image Url / Back Image Url
                  // only -- no other image-shaped field, same policy as the
                  // rest of the site's product images.
                  const imageField =
                    Object.entries(fields).find(
                      ([key, value]) => /front.*image/i.test(key) && typeof value === "string" && value
                    ) ||
                    Object.entries(fields).find(
                      ([key, value]) => /back.*image/i.test(key) && typeof value === "string" && value
                    );
                  image = imageField ? imageField[1] : IMAGE_COMING_SOON;
                }
              } else {
                // Product not found, use item's productId as part number
                partNo = item.productId;
              }

              return {
                ...item,
                image,
                partNo,
                title,
                specSummary,
              };
            } catch (err) {
              console.error("Error fetching product:", item.productId, err);
              return {
                ...item,
                image: IMAGE_COMING_SOON,
                partNo: item.productId,
              };
            }
          })
        );

        // Now fetch category and subcategory names for routing
        const productsWithRoutes = await Promise.all(
          productsWithAllData.map(async (item) => {
            try {
              // Fetch category name
              const categoryRef = doc(db, "product_categories", item.categoryId);
              const categorySnap = await getDoc(categoryRef);
              const categoryName = categorySnap.exists()
                ? categorySnap.data().categoryName
                : "";

              // Fetch subcategory name
              const subCategoryRef = doc(
                db,
                "product_categories",
                item.categoryId,
                "subCategories",
                item.subCategoryId
              );
              const subCategorySnap = await getDoc(subCategoryRef);
              const subCategoryName = subCategorySnap.exists()
                ? subCategorySnap.data().subCategoryName
                : "";

              // Create route — URL slug is based on the product Title,
              // matching how TFTDetailPage resolves the product. Sibling
              // products must be walked in the same order (and given the
              // same duplicate-suffix treatment) as ProductListingPage /
              // TFTDetailPage, or a Title shared with another product in
              // the subcategory would produce the wrong slug.
              // Ordered explicitly by document ID — matches the same order
              // TFTDetailPage/ProductListingPage use for their own
              // productData fetches, so this component's computed slug
              // agrees with wherever it's actually routing to.
              const siblingsSnap = await getDocs(
                query(
                  collection(
                    db, "product_categories", item.categoryId,
                    "subCategories", item.subCategoryId, "productData"
                  ),
                  orderBy(documentId())
                )
              );
              const usedTitleSlugs = new Set();
              let titleSlug = slugify(item.title || subCategoryName);
              siblingsSnap.forEach((d) => {
                const fields = d.data().fields || {};
                const rowTitle = matchField(fields, FIELD_PATTERNS.title) || subCategoryName;

                let rowSlug = slugify(rowTitle);
                if (usedTitleSlugs.has(rowSlug)) {
                  rowSlug = `${rowSlug}-${d.id.slice(0, 6).toLowerCase()}`;
                }
                usedTitleSlugs.add(rowSlug);

                if (d.id === item.productId) titleSlug = rowSlug;
              });

              const categorySlug = slugify(categoryName);
              const subCategorySlug = slugify(subCategoryName);

              const route = `/tft/${categorySlug}/${subCategorySlug}/${titleSlug}`;

              return {
                ...item,
                route,
              };
            } catch (err) {
              console.error("Error fetching category info:", err);
              return {
                ...item,
                route: "#", // Fallback if routing fails
              };
            }
          })
        );

        setProductsWithData(productsWithRoutes);
        setStartIndex(0);
      } catch (err) {
        console.error("Error fetching similar products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [products]);

  const maxStartIndex = Math.max(0, productsWithData.length - visibleCount);
  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex < maxStartIndex;
  const visibleProducts = productsWithData.slice(startIndex, startIndex + visibleCount);

  const goPrev = () => setStartIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStartIndex((i) => Math.min(maxStartIndex, i + 1));

  if (loading) {
    return (
      <div className="frequently-bought-section">
        <h3 className="frequently-bought-title">Similar Products</h3>
        <div className="frequently-bought-grid">
          <div className="frequently-bought-skeleton">Loading products...</div>
        </div>
      </div>
    );
  }

  if (!productsWithData || productsWithData.length === 0) {
    return null;
  }

  const showArrows = productsWithData.length > visibleCount;

  return (
    <div className="frequently-bought-section">
      <h3 className="frequently-bought-title">Similar Prodcucts :</h3>
      <div className="frequently-bought-row">
        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-prev"
            onClick={goPrev}
            disabled={!canGoPrev}
            aria-label="Show previous products"
          >
            ◀
          </button>
        )}

        <div className="frequently-bought-grid">
          {visibleProducts.map((product, index) => {
            const displayName = product.title || product.label || "Product";
            return (
              <div
                key={startIndex + index}
                className="fbt-card"
                onClick={() => {
                  if (product.route && product.route !== "#") {
                    navigate(product.route);
                  }
                }}
              >
                <div className="fbt-card-top">
                  {product.partNo && (
                    <div className="fbt-part-number">{product.partNo}</div>
                  )}
                </div>

                <div className="fbt-card-image">
                  <img
                    src={product.image}
                    alt={displayName}
                    onError={(e) => {
                      e.currentTarget.src = IMAGE_COMING_SOON;
                    }}
                  />
                </div>

                <div className="fbt-card-content">
                  <div className="fbt-title-row">
                    <h3>{displayName}</h3>
                  </div>

                  {product.specSummary && (
                    <p className="fbt-spec-summary">{product.specSummary}</p>
                  )}

                  <div className="fbt-card-info">
                    {product.price && (
                      <div className="fbt-price">
                        Price: ₹{parseFloat(product.price).toFixed(2)}
                      </div>
                    )}
                  </div>

                  <div className="fbt-card-footer">
                    <button
                      className="add-cart-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart({
                          partNo: product.partNo || displayName,
                          title: displayName,
                          image: product.image,
                          quantity: 1,
                          price: parseFloat(product.price) || 0,
                        });
                      }}
                    >
                      <CartIcon size={14} className="add-cart-btn-icon" />
                      Add To Cart
                    </button>

                    <button
                      className="datasheet-icon-btn"
                      aria-label="Download datasheet"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (product.datasheetUrl) {
                          window.open(product.datasheetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-next"
            onClick={goNext}
            disabled={!canGoNext}
            aria-label="Show more products"
          >
            ▶
          </button>
        )}
      </div>
    </div>
  );
};

export default SimilarProducts;